"use client";

import { createContext, useContext, useState, useCallback } from "react";
import {
  supportedCurrencies,
  supportedLanguages,
  type CurrencyCode,
  type LanguageCode,
} from "@/constants/localization";

interface PreferencesContextType {
  currency: CurrencyCode;
  language: LanguageCode;
  setCurrency: (currency: CurrencyCode) => void;
  setLanguage: (language: LanguageCode) => void;
}

export const PreferencesContext = createContext<
  PreferencesContextType | undefined
>(undefined);

// Function to safely get initial value from localStorage with type validation
const getInitialValue = <T extends string>(
  key: string,
  defaultValue: T,
  validValues: T[]
): T => {
  if (typeof window === "undefined") return defaultValue;
  const storedValue = localStorage.getItem(key) as T;
  return storedValue && validValues.includes(storedValue)
    ? storedValue
    : defaultValue;
};

export function PreferencesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Initialize with type-safe default values and validation
  const [currency, setCurrencyState] = useState<CurrencyCode>(() =>
    getInitialValue(
      "currency",
      "USD" as CurrencyCode,
      supportedCurrencies.map((c) => c.code)
    )
  );
  const [language, setLanguageState] = useState<LanguageCode>(() =>
    getInitialValue(
      "language",
      "en" as LanguageCode,
      supportedLanguages.map((l) => l.code)
    )
  );

  const handleSetCurrency = useCallback((newCurrency: CurrencyCode) => {
    // Validate currency
    if (!supportedCurrencies.map((c) => c.code).includes(newCurrency)) {
      console.error(`Unsupported currency: ${newCurrency}`);
      return;
    }

    setCurrencyState(newCurrency);
    localStorage.setItem("currency", newCurrency);
  }, []);

  const handleSetLanguage = useCallback((newLanguage: LanguageCode) => {
    // Validate language
    if (!supportedLanguages.map((l) => l.code).includes(newLanguage)) {
      console.error(`Unsupported language: ${newLanguage}`);
      return;
    }

    setLanguageState(newLanguage);
    localStorage.setItem("language", newLanguage);
  }, []);

  const value = {
    currency,
    language,
    setCurrency: handleSetCurrency,
    setLanguage: handleSetLanguage,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
}
